import {ACCEPTED, OK} from 'http-status-codes';
import _ from 'lodash';
import {
    PriceTier,
    PriceTierSearchParams,
    PriceTierWithHasProductsFlag,
} from '@treez/commons/sharedTypings/priceTier';
import {HistoryEntry} from '@treez/commons/sharedTypings/history';
import {
    Catalog,
    CatalogProduct,
    CatalogProductStatus,
    OwnerType,
    ProductAttributesField,
    ProductData,
    ProductField,
    ProductInCatalog,
    UOM,
} from '@treez/commons/sharedTypings/product';
import callAPI, {Methods} from '~/utils/callAPI';
import {SearchResultsAttributes} from '~/lib/product';
import {SortDirection} from '~/lib/sharedInterfaces';
import {PRODUCT_API_URL} from './Constants';

const {DELETE, GET, PATCH, POST} = Methods;
// Fuzzy as false means exact match, otherwise it will look for similar values as well
export interface SearchFilter {
    field: ProductField | string;
    fuzzy: boolean;
    values: string[] | number[];
}

export interface UpdateProductAttributes {
    action: string;
    catalogIds: number[];
    productIds: number[];
}

/**
 * Note that pageSize defaults to 50 and page defaults to 1. In general
 * orderByRank should be included and true if you're doing any sort of
 * conventional search.  orderByRank can be true when sortBy is included
 * in which case sortBy will be applied first, then rank.
 * */

export interface SortByAttributes {
    field: string;
    order: SortDirection;
}

export interface ProductStorePresenceAttributes {
    status: CatalogProductStatus;
    id: number;
    price: string | null;
    priceTierId: number | null;
    storeName?: string;
}

type ProductId = number;

export type ProductsPresenceAcrossStores = Record<ProductId, ProductStorePresenceAttributes[]>;

type CatalogId = number;

export type CatalogProductInformation = Record<CatalogId, Partial<ProductStorePresenceAttributes>>;

type PriceTierId = number;

export type ReassignPriceTierPayload = Record<PriceTierId, ProductId[]>;

export interface ProductRequirementsAttributes {
    requirements: {
        id: number;
        ownerId?: number;
        ownerType?: string;
        description?: string;
        requirements: {
            name: string;
            type: {
                dataType: string;
                options: string[];
                required: boolean;
                conditionalRequirements: any;
            };
        };
    };
}
export interface SearchCriteria {
    catalogId?: number;
    excludeLinkedProduct?: boolean;
    filters?: SearchFilter[];
    orderByRank?: boolean;
    page?: number;
    pageSize?: number;
    searchString?: string;
    sortBy?: SortByAttributes;
    status?: CatalogProductStatus[];
    weights?: string;
}

export const addStoreCatalog = async (storeId: number, organizationCatalogId: number) => {
    const newCatalogData = {
        name: `Store ${storeId} Catalog`,
        ownerId: storeId,
        ownerType: 'store',
        parentCatalogId: organizationCatalogId,
    } as Catalog;

    return callAPI(`${PRODUCT_API_URL}/catalogs`, POST, newCatalogData);
};

export const addNewProduct = async (productData: Partial<ProductInCatalog>, catalogId: number) => {
    return callAPI(`${PRODUCT_API_URL}/catalogs/${catalogId}/product`, POST, productData);
};

export const editProduct = async (productData: Partial<ProductInCatalog>, catalogId: number) => {
    const {id} = productData;

    return callAPI(`${PRODUCT_API_URL}/catalogs/${catalogId}/products/${id}`, PATCH, productData);
};

export const getAllOrgCatalogs = async (organizationId: number) => {
    return callAPI(
        `${PRODUCT_API_URL}/catalogs?ownerId=${organizationId}&include=childCatalogs&ownerType=organization`
    );
};

export const getCatalogsByOwner = async (
    ownerType: OwnerType,
    ownerId: number
): Promise<Catalog[]> => {
    const response = await callAPI(
        `${PRODUCT_API_URL}/catalogs?ownerId=${ownerId}&ownerType=${ownerType}`
    );

    if (response.status !== OK) {
        console.error(response.status, response.data);
        throw new Error(`Error Retrieving catalog for ${ownerType} with id of ${ownerId}`);
    } else {
        return response.data;
    }
};

export const getBrandProductsById = async (productIds: number[]): Promise<ProductInCatalog[]> => {
    const response = await callAPI(`${PRODUCT_API_URL}/products`, Methods.GET, {
        query: {
            productIds,
            searchBrands: true,
        },
    });

    if (response.status !== OK) {
        console.error(response.status, response.data);
        throw new Error(`Error Retrieving Products from Brand Treez`);
    } else {
        const {data} = response;
        return data.data as ProductInCatalog[];
    }
};

export const getBrandsFromBrandTreez = async (): Promise<string[]> => {
    const response = await callAPI(`${PRODUCT_API_URL}/public/brands`, Methods.GET);

    if (response.status !== OK) {
        console.error(response.status, response.data);
        throw new Error(`Error Retrieving Brands from BrandTreez`);
    } else {
        const {data} = response;
        const brandNames = _.map(data, 'brandName');
        return brandNames as string[];
    }
};

export const getCatalogById = async (catalogId: number): Promise<Catalog> => {
    const {
        data: {data},
    } = await callAPI(`${PRODUCT_API_URL}/catalogs/${catalogId}`);

    return data as Catalog;
};

export const getCatalogsThatCarryProduct = async (organizationId: number, productId: number) => {
    const {data} = await callAPI(
        `${PRODUCT_API_URL}/products/${productId}?include=catalogs&orgId=${organizationId}`
    );

    return data;
};

export const getCatalogProductData = async (
    catalogIds: number[],
    productIds: number[]
): Promise<ProductsPresenceAcrossStores> => {
    if (productIds.length == 0) {
        return {} as ProductsPresenceAcrossStores;
    }

    const {data} = await callAPI(
        `${PRODUCT_API_URL}/catalog_products/?catalogIds=${_.join(
            catalogIds,
            ','
        )}&productIds=${_.join(productIds, ',')}`
    );
    return data as ProductsPresenceAcrossStores;
};

export const getCatalogUniqueProductValues = async (
    catalogId: number,
    productFields: ProductData[]
): Promise<any> => {
    try {
        const attributeFields = _.filter(productFields, (field) => {
            return _.includes(ProductAttributesField, field);
        });

        const topLevelFields = _.filter(productFields, (field) => {
            return _.includes(ProductField, field);
        });

        const fieldQueryString =
            _.size(topLevelFields) > 0 ? `&fields=${topLevelFields.join(',')}` : '';
        const attributesQueryString =
            _.size(attributeFields) > 0 ? `&attributes=${attributeFields.join(',')}` : '';

        const {data} = await callAPI(
            `${PRODUCT_API_URL}/products/distinct?catalogId=${catalogId}${fieldQueryString}${attributesQueryString}`
        );

        return data as Record<ProductData, any[]>;
    } catch (error) {
        console.error(
            `Error Retrieving unique attributes for ${productFields} for catalog ${catalogId}`,
            error
        );
    }
};

export const getOrgPriceTiers = async (orgCatalogId: number): Promise<PriceTier[]> => {
    return searchPriceTiers({
        searchString: '',
        catalogId: orgCatalogId,
        filters: [],
    });
};

export const searchPriceTiers = async (searchParams: PriceTierSearchParams) => {
    const response = await callAPI(
        `${PRODUCT_API_URL}/pricetier/search`,
        Methods.POST,
        searchParams
    );

    const {
        data: {data},
    } = response;
    return data as PriceTierWithHasProductsFlag[];
};

export const getProductsById = async (
    productIds: number[],
    catalogId: number
): Promise<ProductInCatalog[]> => {
    const response = await callAPI(`${PRODUCT_API_URL}/products`, Methods.GET, {
        query: {
            catalogId,
            productIds,
        },
    });

    if (response.status !== OK) {
        console.error(response.status, response.data);
        throw new Error(`Error Retrieving Products from Brand Treez`);
    } else {
        const {data} = response;

        return data as ProductInCatalog[];
    }
};

export const getStoreCatalogs = async (storeId: number) => {
    const catalog = await callAPI(`${PRODUCT_API_URL}/catalogs?ownerId=${storeId}&ownerType=store`);

    return catalog.data.data;
};

export const getValidUomOptions = () => {
    return [UOM.EACH, UOM.FLOZ, UOM.G, UOM.MG, UOM.ML, UOM.OZ];
};

export const patchProduct = async (
    id: number,
    productDiff: Partial<ProductInCatalog>,
    catalogId: number
) => {
    return callAPI(`${PRODUCT_API_URL}/catalogs/${catalogId}/products/${id}`, PATCH, productDiff);
};

export const linkToBrandProduct = async (productId: number, brandsProductId: number) => {
    return callAPI(`${PRODUCT_API_URL}/products/${productId}/link/${brandsProductId}`, PATCH);
};

export const mergeProductsIntoNewProduct = async (
    orgCatalogId: number,
    productIds: number[],
    product: Partial<ProductInCatalog>,
    catalogIds: number[],
    catalogProductInformation: CatalogProductInformation
) => {
    return callAPI(`${PRODUCT_API_URL}/catalogs/${orgCatalogId}/products/merge`, POST, {
        product,
        productIds,
        catalogIds,
        catalogProductInformation,
    });
};

export const mergeProductsIntoExistingProduct = async (
    productIdToMergeTo: number,
    orgCatalogId: number,
    productIds: number[],
    catalogIds: number[],
    catalogProductInformation: CatalogProductInformation
) => {
    return callAPI(
        `${PRODUCT_API_URL}/catalogs/${orgCatalogId}/products/merge/${productIdToMergeTo}`,
        POST,
        {
            productIds,
            catalogIds,
            catalogProductInformation,
        }
    );
};

export const exportProductAPI = async (searchObject: SearchCriteria) => {
    const searchPath = `${PRODUCT_API_URL}/products/download`;
    const params = {
        query: searchObject,
    };

    const {data} = await callAPI(searchPath, Methods.GET, params);
    return data;
};

export const searchProductAPI = async (searchObject: SearchCriteria) => {
    const searchPath = `${PRODUCT_API_URL}/products/`;
    const params = {
        query: searchObject,
    };

    const {data} = await callAPI(searchPath, Methods.GET, params);
    return data as SearchResultsAttributes;
};

export const searchCatalogForProducts = async (catalogId: number, searchObject: SearchCriteria) => {
    searchObject.catalogId = Number(catalogId);
    return searchProductAPI(searchObject);
};

export const searchExistingLinks = async (catalogId: number, productIds: number[]) => {
    const searchObject = {
        filters: [
            {
                field: ProductField.LINKED_TO,
                fuzzy: false,
                values: productIds,
            },
        ],
    };

    const {data: existingLinks} = await searchCatalogForProducts(catalogId, searchObject);

    return existingLinks;
};

export const addProductsToCatalog = async (
    catalogId: number,
    productIds: number[]
): Promise<ProductInCatalog[]> => {
    const {data} = await callAPI(`${PRODUCT_API_URL}/catalogs/${catalogId}/products`, PATCH, {
        productIds,
    });

    return data as ProductInCatalog[];
};

export const addProductsToCatalogs = async (catalogIds: number[], productIds: number[]) => {
    const responses = await Promise.all(
        _.map(catalogIds, async (value) => {
            return callAPI(`${PRODUCT_API_URL}/catalogs/${value}/products`, PATCH, {
                productIds,
            });
        })
    );

    return responses;
};

export const removeProductsFromCatalogs = async (catalogIds: number[], productIds: number[]) => {
    const responses = await Promise.all(
        _.map(catalogIds, async (value) => {
            return callAPI(`${PRODUCT_API_URL}/catalogs/${value}/products`, DELETE, {
                productIds,
            });
        })
    );

    return responses;
};

export const unlinkBrandProduct = async (productId: number) => {
    return callAPI(`${PRODUCT_API_URL}/products/${productId}/unlink`, PATCH);
};

export const updateCatalogProduct = async (
    catalogId: number,
    productId: number,
    updates: Partial<ProductInCatalog>
): Promise<void> => {
    await callAPI(`${PRODUCT_API_URL}/catalogs/${catalogId}/override/${productId}`, PATCH, updates);
};

export const bulkUpdateCatalogProductStatus = async (
    centralCatalogId: number,
    productId: number,
    status: CatalogProductStatus
): Promise<CatalogProduct[]> => {
    const {data} = await callAPI(`${PRODUCT_API_URL}/catalog_products/`, PATCH, {
        centralCatalogId,
        status,
        productIds: [productId],
    });

    return data.data as CatalogProduct[];
};

export const getPriceTier = async (
    priceTierId: number,
    catalogId: number
): Promise<PriceTierWithHasProductsFlag> => {
    const {data} = await callAPI(
        `${PRODUCT_API_URL}/pricetier/${priceTierId}/catalog/${catalogId}`,
        Methods.GET
    );

    return data.data;
};

export const updatePriceTier = async (priceTier: Partial<PriceTier>, catalogId: number) => {
    return callAPI(
        `${PRODUCT_API_URL}/pricetier/${priceTier.id}/catalog/${catalogId}`,
        Methods.PUT,
        priceTier
    );
};

export const createPriceTier = async (priceTier: Partial<PriceTier>, catalogId: number) => {
    return callAPI(`${PRODUCT_API_URL}/pricetier/catalog/${catalogId}`, POST, priceTier);
};

export const reassignPriceTiers = async (payload: ReassignPriceTierPayload, catalogId: number) => {
    return callAPI(
        `${PRODUCT_API_URL}/catalog_products/pricetiers/reassign/${catalogId}`,
        PATCH,
        payload
    );
};

export const getProductsAssignedToPriceTier = async (
    priceTierId: number,
    orgCatalogId: number
): Promise<ProductInCatalog[]> => {
    const {data} = await callAPI(
        `${PRODUCT_API_URL}/pricetier/${priceTierId}/products?orgCatalogId=${orgCatalogId}`,
        GET
    );

    return data.data;
};

export type PriceTierCatalogPresence = Record<PriceTierId, CatalogId[]>;
export const getPriceTierPresenceInCatalog = async (
    priceTierIds: number[]
): Promise<PriceTierCatalogPresence> => {
    const {data} = await callAPI(
        `${PRODUCT_API_URL}/pricetier/catalogs?priceTierIds=${priceTierIds.map(String)}`,
        GET
    );

    return data.data;
};

export const clearOverride = async (
    productId: number,
    catalogId: number,
    fields: ProductData[]
) => {
    try {
        const res = await callAPI(
            `${PRODUCT_API_URL}/catalogs/${catalogId}/products/${productId}/override`,
            DELETE,
            {
                fields,
            }
        );

        if (res.status !== ACCEPTED) {
            console.error('Error clearing override.', res);
            throw new Error('Error clearing override.');
        }

        return res.data;
    } catch (error) {
        console.error(error);
        throw new Error('Error clearing override.');
    }
};

export const getProductHistory = async (
    productId: number,
    catalogId: number
): Promise<HistoryEntry[]> => {
    const {data} = await callAPI(
        `${PRODUCT_API_URL}/catalogs/${catalogId}/products/${productId}/history`,
        GET
    );

    return data;
};
